import { useEffect, useState, type CSSProperties } from 'react';
import { api } from './api';
import {
  startDismissedKey,
  startProgress,
  startSteps,
  type StartCounts,
  type StartView,
} from './startSteps';
import { Icon } from './icons';

interface Props {
  companyId: string;
  canManageUsers: boolean;
  onOpen: (view: StartView) => void;
}

/** عدد عناصر قائمة، أو null حين لا تُقرأ (لا صلاحية) — فتختفى خطوتها بدل أن تبدو ناقصة. */
async function countOf(load: () => Promise<readonly unknown[]>): Promise<number | null> {
  try {
    return (await load()).length;
  } catch {
    return null;
  }
}

function isDismissed(companyId: string): boolean {
  try {
    return window.localStorage.getItem(startDismissedKey(companyId)) === '1';
  } catch {
    return false;
  }
}

/** كارت «خطوات البداية» على الرئيسية — يختفى بنفسه حين تكتمل الخطوات أو يخفيه المستخدم. */
export default function GettingStarted({ companyId, canManageUsers, onOpen }: Props) {
  const [counts, setCounts] = useState<StartCounts | null>(null);
  const [dismissed, setDismissed] = useState(() => isDismissed(companyId));

  useEffect(() => {
    if (dismissed) return;
    let alive = true;
    void Promise.all([
      countOf(() => api.customers(companyId)),
      countOf(() => api.catalogItems(companyId)),
      countOf(() => api.salesInvoices(companyId)),
      countOf(() => api.suppliers(companyId)),
      countOf(() => api.cashExpenses(companyId)),
      canManageUsers ? countOf(() => api.members(companyId)) : Promise.resolve(null),
    ]).then(([customers, items, invoices, suppliers, expenses, members]) => {
      if (alive) setCounts({ customers, items, invoices, suppliers, expenses, members });
    });
    return () => {
      alive = false;
    };
  }, [companyId, canManageUsers, dismissed]);

  if (dismissed || !counts) return null;
  const steps = startSteps(counts);
  const { done, total } = startProgress(steps);
  if (total === 0 || done === total) return null;
  const next = steps.find((step) => !step.done);
  const percent = Math.round((done / total) * 100);

  const dismiss = () => {
    try {
      window.localStorage.setItem(startDismissedKey(companyId), '1');
    } catch {
      // التخزين محجوب: يختفى الكارت لهذه الجلسة فقط
    }
    setDismissed(true);
  };

  return (
    <section className="getting-started" aria-labelledby="getting-started-title">
      <header className="gs-head">
        <div
          className="gs-ring"
          role="img"
          aria-label={`${done.toLocaleString('ar-EG')} من ${total.toLocaleString('ar-EG')}`}
          style={{ '--gs-percent': percent + '%' } as CSSProperties}
        >
          <span>
            {done.toLocaleString('ar-EG')}/{total.toLocaleString('ar-EG')}
          </span>
        </div>
        <div className="gs-title">
          <h2 id="getting-started-title">خطوات البداية</h2>
          <p>أول شغل فعلى على حساباتك — كل خطوة بتتعلّم لوحدها لما تعملها.</p>
        </div>
        <button type="button" className="ghost gs-dismiss" onClick={dismiss}>
          إخفاء
        </button>
      </header>

      <ol className="gs-steps">
        {steps.map((step) => (
          <li key={step.id} className={step.done ? 'done' : step === next ? 'next' : undefined}>
            <button
              type="button"
              className="gs-step"
              onClick={() => onOpen(step.id)}
              aria-current={step === next ? 'step' : undefined}
            >
              <span className="gs-icon" aria-hidden="true">
                <Icon name={step.done ? 'check' : step.icon} size={18} />
              </span>
              <span className="gs-text">
                <strong>{step.title}</strong>
                <span>{step.done ? 'تمت ✓' : step.hint}</span>
              </span>
              {!step.done && <Icon name="chevron" size={18} />}
            </button>
          </li>
        ))}
      </ol>
    </section>
  );
}
